import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  PaymentReport, PaymentDetailExport, PaymentBalancingReport, ReportScope,
} from '../../models/payment-report.model';
import { PaymentDto } from '../../models/payment.model';
import { Page } from '../../models/page.model';

export interface PaymentSearchParams {
  receiptNumber?: string;
  customerName?: string;
  statuses?: string[];
  page?: number;
  size?: number;
}

/** Client for the admin payment reporting endpoints (/api/admin/payments). */
@Injectable({ providedIn: 'root' })
export class PaymentReportService {
  private readonly baseUrl = `${environment.apiUrl}/api/admin/payments`;

  constructor(private readonly http: HttpClient) {}

  searchPayments(search: PaymentSearchParams): Observable<Page<PaymentDto>> {
    let params = new HttpParams()
      .set('page', String(search.page ?? 0))
      .set('size', String(search.size ?? 10));
    if (search.receiptNumber) params = params.set('receiptNumber', search.receiptNumber);
    if (search.customerName) params = params.set('customerName', search.customerName);
    search.statuses?.forEach(s => params = params.append('statuses', s));
    return this.http.get<Page<PaymentDto>>(this.baseUrl, { params });
  }

  getSummary(scope: ReportScope): Observable<PaymentReport> {
    let params = new HttpParams()
      .set('dateFrom', scope.dateFrom ?? '')
      .set('dateTo', scope.dateTo ?? '');
    if (scope.officeId) params = params.set('officeId', scope.officeId);
    if (scope.cashierUserId) params = params.set('cashierUserId', scope.cashierUserId);
    return this.http.get<PaymentReport>(`${this.baseUrl}/reports`, { params });
  }

  getDetail(scope: ReportScope, cap = 1000): Observable<PaymentDetailExport> {
    let params = new HttpParams()
      .set('dateFrom', scope.dateFrom ?? '')
      .set('dateTo', scope.dateTo ?? '')
      .set('cap', String(cap));
    if (scope.officeId) params = params.set('officeId', scope.officeId);
    if (scope.cashierUserId) params = params.set('cashierUserId', scope.cashierUserId);
    return this.http.get<PaymentDetailExport>(`${this.baseUrl}/reports/detail`, { params });
  }

  getBalancing(businessDate: string, scope: ReportScope, groupBy: 'user' | 'drawer'): Observable<PaymentBalancingReport> {
    let params = new HttpParams()
      .set('businessDate', businessDate)
      .set('groupBy', groupBy);
    if (scope.officeId) params = params.set('officeId', scope.officeId);
    if (scope.cashierUserId) params = params.set('cashierUserId', scope.cashierUserId);
    return this.http.get<PaymentBalancingReport>(`${this.baseUrl}/reports/balancing`, { params });
  }
}
